import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ICostCenter } from '../../../../common/data/dtos/coster-center';
import { IJobTitle } from '../../../../common/data/dtos/job-title';
import { IWorkResourceJobTitle } from '../../../../common/data/dtos/work-resource-job-title';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Input from '../../../../components/bootstrap/forms/Input';
import Select from '../../../../components/bootstrap/forms/Select';
import Textarea from '../../../../components/bootstrap/forms/Textarea';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import costCenterService from '../../../../services/cost-center-service';
import jobTitleService from '../../../../services/job-title-service';

type JobTitleFormValues = {
    id?: string;
    name: string;
    description: string;
    occupationCode?: string;
    costCenterId: string;
    department?: string;
    workResourcesJobTitles?: IWorkResourceJobTitle[];
};

interface Props {
    isModalOpened: boolean;
    selectedJobTitle: IJobTitle | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const JobTitleModal = ({ isModalOpened, selectedJobTitle, onModalClose, onAfterSubmit }: Props) => {
    const formik = useFormik({
        initialValues: {
            name: selectedJobTitle?.name || '',
            description: selectedJobTitle?.description || '',
            occupationCode: selectedJobTitle?.occupationCode || '',
            department: selectedJobTitle?.department || '',
            costCenterId: selectedJobTitle?.costCenterId || '',
        },

        validate: (values) => {
            const errors: {
                name?: string;
                costCenterId?: string;
            } = {};

            if (!values.name) errors.name = MESSAGES.VALIDATION.REQUIRED_FIELD;

            if (!values.costCenterId) errors.costCenterId = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [costCenters, setCostCenters] = useState<ICostCenter[]>([]);

    useEffect(() => {
        resetForm();

        if (selectedJobTitle) setFieldValues(selectedJobTitle);

        fetchCostCenters();
    }, [isModalOpened]);

    const fetchCostCenters = async () => {
        setIsSubmitting(true);

        const response = await costCenterService.getCostCenters();

        setIsSubmitting(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        setCostCenters(response.costCenters || []);
    };

    const onFormSubmit = async (values: JobTitleFormValues) => {
        if (selectedJobTitle) {
            setIsSubmitting(true);

            const response = await jobTitleService.updateJobTitle({
                id: selectedJobTitle.id,
                name: values.name,
                description: values.description,
                occupationCode: values.occupationCode,
                department: values.department,
                costCenterId: values.costCenterId,
                rowVersion: selectedJobTitle.rowVersion,
                status: selectedJobTitle.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await jobTitleService.createJobTitle({
                name: values.name,
                description: values.description,
                occupationCode: values.occupationCode,
                department: values.department,
                costCenterId: values.costCenterId,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                name: '',
                description: '',
                occupationCode: '',
                costCenterId: '',
                department: '',
            },
        });
    };

    const setFieldValues = (values: IJobTitle) => {
        formik.setFieldValue('name', values.name || '');
        formik.setFieldValue('description', values.description || '');
        formik.setFieldValue('occupationCode', values.occupationCode || '');
        formik.setFieldValue('department', values.department || '');
        formik.setFieldValue('costCenterId', values.costCenterId || '');
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop isScrollable>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id="job-title-modal">Manutenção de funções</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor="primary">
                    <CardTabItem id="tab-item-1" title="Dados da Função" icon="Work">
                        <div className="row g-4">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="name" label="Nome" isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.name}
                                        invalidFeedback={formik.errors.name}
                                        validFeedback=""
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="description" label="Descrição" isFloating>
                                    <Textarea onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.description} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="department" label="Setor" isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.department} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="occupationCode" label="CBO" isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.occupationCode} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="costCenterId" label="Centro de Custo" isFloating>
                                    <Select
                                        ariaLabel="Escolha um centro de custo"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.costCenterId}
                                        list={costCenters.map((costCenter) => ({
                                            value: costCenter.id,
                                            text: `${costCenter.description}`,
                                        }))}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.costCenterId}
                                        invalidFeedback={formik.errors.costCenterId}
                                        validFeedback=""
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className="bg-transparent">
                <CardFooterRight>
                    <Button color="success" className="w-100" onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};
